import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  MapPin,
  CreditCard,
  PieChart,
  Settings,
  LogOut,
  ChevronDown,
  ListTodo,
  FileText,
  Mail,
  Contact,
  BarChart3,
  FileSignature,
  CalendarClock
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const Layout = () => {
  const { user, logout, isFounder } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
    { to: "/clients", icon: Users, label: "Clients" },
    { to: "/projects", icon: FolderKanban, label: "Projects" },
    { to: "/tasks", icon: ListTodo, label: "To-Do" },
    { to: "/site-visits", icon: MapPin, label: "Site Visits" },
    { to: "/payments", icon: CreditCard, label: "Payments" },
    { to: "/campaigns", icon: Mail, label: "Campaigns" },
    { to: "/scheduling", icon: CalendarClock, label: "Scheduling" },
    { to: "/contacts", icon: Contact, label: "Contacts" },
    { to: "/contracts", icon: FileSignature, label: "Contracts" },
  ];

  if (isFounder) {
    navItems.push({ to: "/accounts", icon: PieChart, label: "Accounts" });
    navItems.push({ to: "/performance", icon: BarChart3, label: "Performance" });
    navItems.push({ to: "/audit-log", icon: FileText, label: "Audit Log" });
  }

  return (
    <div className="app-container">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="sidebar-logo" style={{ padding: '20px 16px', textAlign: 'left' }}>
          <h1 style={{ 
            fontFamily: "'Playfair Display', Georgia, serif", 
            fontSize: '24px', 
            fontWeight: 700, 
            color: 'white', 
            letterSpacing: '1px',
            margin: 0,
            lineHeight: 1.2
          }}>
            Tat atelier<span style={{ color: '#FF4500' }}>.</span>
          </h1>
          <p style={{ 
            fontFamily: "'Montserrat', sans-serif", 
            fontSize: '9px', 
            fontWeight: 400, 
            color: 'rgba(255,255,255,0.35)', 
            letterSpacing: '3px',
            textTransform: 'uppercase',
            marginTop: '6px'
          }}>
            The architecture tale
          </p>
        </div>

        <nav className="sidebar-nav">
          <div className="nav-section">
            <div className="nav-section-title">Main Menu</div>
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `nav-item ${isActive ? "active" : ""}`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(" ", "-")}`}
              >
                <item.icon />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Settings</div>
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `nav-item ${isActive ? "active" : ""}`
              }
              data-testid="nav-settings"
            >
              <Settings />
              <span>Settings</span>
            </NavLink>
          </div>
        </nav>

        {/* User section at bottom */}
        <div className="p-4 border-t border-[#1A1A1A]">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 p-2 rounded hover:bg-[#1A1A1A] transition-colors" data-testid="user-menu-trigger">
                <div className="user-avatar">
                  {user?.name?.charAt(0).toUpperCase() || "U"}
                </div>
                <div className="flex-1 text-left">
                  <div className="text-sm font-medium text-white truncate">
                    {user?.name || "User"}
                  </div>
                  <div className="text-xs text-gray-500 capitalize">
                    {user?.role?.replace("_", " ") || "User"}
                  </div>
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-[#1A1A1A] border-[#262626]">
              <DropdownMenuItem className="text-gray-400 focus:bg-[#262626] focus:text-white">
                <span className="text-xs">{user?.email}</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#262626]" />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-400 focus:bg-[#262626] focus:text-red-400 cursor-pointer"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Main Content with Page Transition */}
      <main className="main-content scroll-smooth">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 12, scale: 0.995 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.995 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Mobile Bottom Navigation - Pro App Style */}
      <nav className="mobile-nav" data-testid="mobile-nav">
        <NavLink to="/dashboard" className={({ isActive }) => `mobile-nav-item touch-feedback ${isActive ? 'active' : ''}`}>
          <LayoutDashboard />
          <span>Home</span>
        </NavLink>
        <NavLink to="/clients" className={({ isActive }) => `mobile-nav-item touch-feedback ${isActive ? 'active' : ''}`}>
          <Users />
          <span>Clients</span>
        </NavLink>
        <NavLink to="/tasks" className={({ isActive }) => `mobile-nav-item touch-feedback ${isActive ? 'active' : ''}`}>
          <ListTodo />
          <span>Tasks</span>
        </NavLink>
        <NavLink to="/payments" className={({ isActive }) => `mobile-nav-item touch-feedback ${isActive ? 'active' : ''}`}>
          <CreditCard />
          <span>Payments</span>
        </NavLink>
        <div className="mobile-nav-item mobile-nav-more touch-feedback">
          <ChevronDown />
          <span>More</span>
          <div className="mobile-nav-menu">
            <NavLink to="/projects" className={({ isActive }) => `touch-feedback ${isActive ? 'active' : ''}`}>
              <FolderKanban className="w-4 h-4" />
              Projects
            </NavLink>
            <NavLink to="/site-visits" className={({ isActive }) => `touch-feedback ${isActive ? 'active' : ''}`}>
              <MapPin className="w-4 h-4" />
              Site Visits
            </NavLink>
            <NavLink to="/campaigns" className={({ isActive }) => `touch-feedback ${isActive ? 'active' : ''}`}>
              <Mail className="w-4 h-4" />
              Campaigns
            </NavLink>
            <NavLink to="/scheduling" className={({ isActive }) => `touch-feedback ${isActive ? 'active' : ''}`}>
              <CalendarClock className="w-4 h-4" />
              Scheduling
            </NavLink>
            <NavLink to="/contacts" className={({ isActive }) => `touch-feedback ${isActive ? 'active' : ''}`}>
              <Contact className="w-4 h-4" />
              Contacts
            </NavLink>
            {isFounder && (
              <>
                <NavLink to="/accounts" className={({ isActive }) => `touch-feedback ${isActive ? 'active' : ''}`}>
                  <PieChart className="w-4 h-4" />
                  Accounts
                </NavLink>
              </>
            )}
            <NavLink to="/settings" className={({ isActive }) => `touch-feedback ${isActive ? 'active' : ''}`}>
              <Settings className="w-4 h-4" />
              Settings
            </NavLink>
            <button onClick={handleLogout} className="logout-btn touch-feedback">
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>
      </nav>
    </div>
  );
};

export default Layout;
